import {getIntersectCordsBetweenLines, getMidPoint, getPointDistance} from "./sqftHelper";
import polygonClipping from "polygon-clipping";

/**
 * Get point position left or right from the line
 *  if D = 0 - then searchedPoint is on the line
 *  if D < 0 - then searchedPoint is on the left from the line
 *  if D > 0 - then searchedPoint is on the right from the line
 * @param linePoint1 {{x: int, y: int}}
 * @param linePoint2 {{x: int, y: int}}
 * @param searchedPoint {{x: int, y: int}}
 */
export const getThePositionOfAPointToTheLeftOrRightOfALine = (linePoint1, linePoint2, searchedPoint) => {
  return (searchedPoint.x - linePoint1.x) * (linePoint2.y - linePoint1.y) - (searchedPoint.y - linePoint1.y) * (linePoint2.x - linePoint1.x)
}

function crossProduct(ax, ay, bx, by) {
  return ax * by - ay * bx;
}

function direction(ax, ay, bx, by, cx, cy) {
  return crossProduct(cx - ax, cy - ay, bx - ax, by - ay);
}

function onSegment(ax, ay, bx, by, cx, cy) {
  const withinX = (ax <= cx && cx <= bx) || (ax >= cx && cx >= bx);
  const withinY = (ay <= cy && cy <= by) || (ay >= cy && cy >= by);
  return withinX && withinY;
}

/**
 * Tests if line a-b intersects with line c-d, allow overlap will return false if lines are over each other
 * @param ax
 * @param ay
 * @param bx
 * @param by
 * @param cx
 * @param cy
 * @param dx
 * @param dy
 * @param allowOverlap
 */
export const getIntersectionCordsV2 = (ax, ay, bx, by, cx, cy, dx, dy, allowOverlap = true) => {
  const d1 = direction(ax, ay, bx, by, cx, cy);
  const d2 = direction(ax, ay, bx, by, dx, dy);
  const d3 = direction(cx, cy, dx, dy, ax, ay);
  const d4 = direction(cx, cy, dx, dy, bx, by);

  if (d1 * d2 < 0 && d3 * d4 < 0) {
    const A1 = by - ay;
    const B1 = ax - bx;
    const C1 = A1 * ax + B1 * ay;
    const A2 = dy - cy;
    const B2 = cx - dx;
    const C2 = A2 * cx + B2 * cy;

    const det = A1 * B2 - A2 * B1;
    if (det === 0) { // Collinear lines
      return null;
    } else {
      const x = (B2 * C1 - B1 * C2) / det;
      const y = (A1 * C2 - A2 * C1) / det;
      return [x, y];
    }
  }

  if (!allowOverlap && d1 === 0 && d2 === 0 && (onSegment(ax, ay, bx, by, cx, cy) || onSegment(ax, ay, bx, by, dx, dy))) {
    return null;  //overlap each other
  }

  if ((d1 === 0 && onSegment(ax, ay, bx, by, cx, cy)) ||
    (d2 === 0 && onSegment(ax, ay, bx, by, dx, dy)) ||
    (d3 === 0 && onSegment(cx, cy, dx, dy, ax, ay)) ||
    (d4 === 0 && onSegment(cx, cy, dx, dy, bx, by))) {
    return null; //overlap only in one point
  }

  return null;
}

// Define a function to calculate the cross product of two vectors in 2D.
export function crossProduct2D(vectorA, vectorB) {
  // Calculate the cross product. Since the vectors are in 2D, the cross product will be a scalar.
  // vectorA.x * vectorB.y - vectorA.y * vectorB.x gives the Z component of the 3D cross product.
  return vectorA.x * vectorB.y - vectorA.y * vectorB.x;
}

/**
 * Finds the line in the `lines` array that is closest to referenceLine
 * and intersects the perpendicular line drawn from midpoint of referenceLine.
 *
 * @param {Line} referenceLine The line from which the perpendicular is drawn.
 * @param {Line[]} lines The array of lines to search in.
 * @return {{Line, point} | null} The found line or null if no line is found.
 */
export function findClosestIntersectingPointAndLine(referenceLine, lines) {
  // Find the perpendicular line to referenceLine
  const perpendicularVector = getPerpendicularMidpointVector(referenceLine);

  // Find all intersection points of the perpendicular line with the lines in the array
  const intersectionPointsAndLines = [];
  for (const line of lines) {
    // skip referenceLine
    if (line.id === referenceLine.id) {
      continue
    }
    //const intersectionPoint = getVectorIntersectionPoint(perpendicularVector, line);
    const intersectionPoint = getIntersectCordsBetweenLines(perpendicularVector.from, perpendicularVector.to, line.from, line.to, true ,true);

    if (intersectionPoint) {
      intersectionPointsAndLines.push({
        line,
        point: intersectionPoint,
      });
    }
  }

  return getClosestLineAndIntersectionPoint(referenceLine, intersectionPointsAndLines)
}

/**
 * Finds the line in the `lines` array that is closest to referenceLine
 * and intersects the perpendicular line drawn from midpoint of referenceLine.
 *
 * @return {{Line, point} | null} The found line or null if no line is found.
 * @param line
 * @param intersectionPointsAndLines
 */
export const getClosestLineAndIntersectionPoint = (line, intersectionPointsAndLines) => {
  let closestIntersectionPointAndLine = null;
  let minDistance = Infinity;
  for (const intersection of intersectionPointsAndLines) {
    const midPoint = getMidPoint(line.from, line.to);
    const distance = getPointDistance(midPoint, intersection.point);
    if (distance < minDistance) {
      minDistance = distance;
      closestIntersectionPointAndLine = intersection;
    }
  }

  return closestIntersectionPointAndLine;
}

export const getPerpendicularMidpointVector = (line) => {
  const midPoint = getMidPoint(line.from, line.to);
  const vector = {
    x: line.from.y - line.to.y, // Изменение координат Y для получения перпендикуляра
    y: line.to.x - line.from.x, // Изменение координат X для получения перпендикуляра
  };

  // Вектор, начинающийся в серединной точке и проходящий через нее
  return {
    from: midPoint,
    to: {
      x: midPoint.x + vector.x,
      y: midPoint.y + vector.y,
    },
  };
}

// Function to find the convex hull using Graham's algorithm
export const convexHull = (points) => {
  // Sort points by Y (ascending) and then by X
  points.sort((a, b) => a.y === b.y ? a.x - b.x : a.y - b.y);
  // Function for calculating rotation: >0 if CCW, <0 if CW, 0 if collinear
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower = [];
  for (let i = 0; i < points.length; i++) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], points[i]) <= 0) {
      lower.pop();
    }
    lower.push(points[i]);
  }

  const upper = [];
  for (let i = points.length - 1; i >= 0; i--) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], points[i]) <= 0) {
      upper.pop();
    }
    upper.push(points[i]);
  }

  // Deleting the last point of each half-shell (it is duplicated)
  lower.pop();
  upper.pop();

  // Concatenation of lower and upper shell
  return lower.concat(upper);
}

/////////////////////////////////////////////////////////////
// function buildGraph(shapes) {
//   const graph = new Map();
//
//   // Создаем узлы графа для каждой уникальной точки
//   for (const shape of shapes) {
//     for (let point of shape) {
//       point = roundPoint(point); // округляем точку
//       const key = `${point.x},${point.y}`;
//       if (!graph.has(key)) {
//         graph.set(key, {
//           ...point,
//           edges: new Set()
//         });
//       }
//     }
//   }
//
//   // Добавляем рёбра между точками каждого шейпа
//   for (const shape of shapes) {
//     for (let i = 0; i < shape.length; i++) {
//       const current = roundPoint(shape[i]);
//       const next = roundPoint(shape[(i + 1) % shape.length]); // замкнутый контур
//       const currentKey = `${current.x},${current.y}`;
//       const nextKey = `${next.x},${next.y}`;
//
//       graph.get(currentKey).edges.add(nextKey);
//       graph.get(nextKey).edges.add(currentKey);
//     }
//   }
//
//   return graph;
// }
//
// function findLeftBottomVertex(graph) {
//   let minVertex = null;
//   let minKey = null;
//   graph.forEach((value, key) => {
//     if (minVertex === null || value.y < minVertex.y || (value.y === minVertex.y && value.x < minVertex.x)) {
//       minVertex = value;
//       minKey = key;
//     }
//   });
//   return minKey;
// }
//
// function findContours(shapes) {
//   const graph = buildGraph(shapes);
//   const startKey = findLeftBottomVertex(graph);
//   let currentKey = startKey;
//   let previousKey = null;
//   const result = [];
//
//   do {
//     result.push(currentKey.split(',').map(Number)); // Добавляем текущую точку в контур
//     const currentVertex = graph.get(currentKey);
//     let nextKey = null;
//     let maxAngle = -Infinity;
//
//     // Перебираем возможные следующие вершины
//     currentVertex.edges.forEach(key => {
//       if (key !== previousKey) { // Исключаем вернуться назад
//         const angle = calculateAngle(currentVertex, graph.get(previousKey), graph.get(key));
//         if (angle > maxAngle) {
//           maxAngle = angle;
//           nextKey = key;
//         }
//       }
//     });
//
//     previousKey = currentKey;
//     currentKey = nextKey;
//   } while (currentKey !== startKey);
//
//   return result.map(([x, y]) => ({ x, y }));
// }
//
// function calculateAngle(current, previous, next) {
//   if (!previous) return 0; // Если предыдущая точка не задана, угол не вычисляется
//
//   const vectorA = { x: current.x - previous.x, y: current.y - previous.y };
//   const vectorB = { x: next.x - current.x, y: next.y - current.y };
//   const dotProduct = vectorA.x * vectorB.x + vectorA.y * vectorB.y;
//   const magnitudeA = Math.sqrt(vectorA.x ** 2 + vectorA.y ** 2);
//   const magnitudeB = Math.sqrt(vectorB.x ** 2 + vectorB.y ** 2);
//
//   // Косинус угла между векторами
//   const cosTheta = dotProduct / (magnitudeA * magnitudeB);
//
//   // Возвращаем угол в радианах
//   return Math.acos(cosTheta);
// }

/**
 * Returns rounded shape points
 * @param {Array} shapesPoints shapes array
 * @returns {Array} shapes array with rounded points
 */
export const roundShapesPoints = (shapesPoints) => {
  return shapesPoints.map(shape =>
    roundShapePoints(shape)
  );
};

export const roundShapePoints = (shapePoints) => {
  return shapePoints.map(point => roundPoint(point));
};

const roundPoint = (point) => {
  const precision = 1000000; // Round to 6 digits after comma
  return {
    x: Math.round(point.x * precision) / precision,
    y: Math.round(point.y * precision) / precision,
    id: point.id
  };
};

// Convert shapes to polygon-clipping format
export function convertShapesToPolygons(shapesPoints) {
  return shapesPoints.map(shapePoints => [convertShapeToPolygon(shapePoints)]);
}

export function convertShapeToPolygon(shapePoints) {
  return shapePoints.map(point => [point.x, point.y]);
}

// Convert points from polygon-clipping format to shapes format
export function convertFromPolygon(polygons) {
  if (!Array.isArray(polygons) || polygons.length === 0) {
    return [];
  }

  return polygons.map(polygon =>
    polygon[0].map(coord => ({ x: coord[0], y: coord[1] }))
  );
}


/**
 * Function to calculate the outline of multiple shapes.
 * Each shape is defined by an array of points, which are connected together.
 * The function rounds the coordinates to a certain precision to handle slight mismatches.
 *
 * @param {Array} shapes - Array of shapes, each shape is an array of points.
 * @returns {Array} - Array of points forming the unified outline of the shapes.
 */
export const unifyShapePoints = (shapes) => {
  const roundedPoints = roundShapesPoints(shapes);
  const polygons = convertShapesToPolygons(roundedPoints);
  const result = polygonClipping.union(polygons);

  // console.log("roundedPoints",roundedPoints);
  // console.log("polygons",polygons);
  // console.log("result",result);
  // console.log("convertFromPolygon(result);",convertFromPolygon(result));
  return convertFromPolygon(result);
}

// Function to remove consecutive duplicate points
export function removeConsecutiveDuplicates(points) {
  // Filter the points array to remove consecutive duplicates
  return points.filter((point, index, array) => {
    // Check if the current point is different from the previous one
    if (index === 0) {
      return true; // Always include the first point
    }
    const prevPoint = array[index - 1];
    return point.x !== prevPoint.x || point.y !== prevPoint.y;
  });
}

export function areCollinear(p1, p2, p3, p4) {
  // Check that the vector products (p2-p1) and (p4-p3) are equal to zero
  return (p2.x - p1.x) * (p4.y - p3.y) === (p4.x - p3.x) * (p2.y - p1.y) &&
    ((Math.min(p1.x, p2.x) <= Math.max(p3.x, p4.x) && Math.max(p1.x, p2.x) >= Math.min(p3.x, p4.x)) &&
      (Math.min(p1.y, p2.y) <= Math.max(p3.y, p4.y) && Math.max(p1.y, p2.y) >= Math.min(p3.y, p4.y)));
}

export function getSegmentIntersectionPoint(seg1, seg2) {
  const a1 = seg1.end.y - seg1.start.y;
  const b1 = seg1.start.x - seg1.end.x;
  const c1 = a1 * seg1.start.x + b1 * seg1.start.y;

  const a2 = seg2.end.y - seg2.start.y;
  const b2 = seg2.start.x - seg2.end.x;
  const c2 = a2 * seg2.start.x + b2 * seg2.start.y;

  const determinant = a1 * b2 - a2 * b1;

  if (determinant === 0) {
    return null;
  } else {
    const x = (c1 * b2 - c2 * b1) / determinant;
    const y = (a1 * c2 - a2 * c1) / determinant;
    return { x, y };
  }
}

export function isPointOnSegment(pt, seg) {
  return Math.min(seg.start.x, seg.end.x) <= pt.x && pt.x <= Math.max(seg.start.x, seg.end.x) &&
    Math.min(seg.start.y, seg.end.y) <= pt.y && pt.y <= Math.max(seg.start.y, seg.end.y);
}

/**
 * Return the length of the overlap of the segments
 */
function getOverlapLength(seg1, seg2) {
  if (seg1.start.x === seg1.end.x && seg1.start.x === seg2.start.x) {
    const sortedY1 = [seg1.start.y, seg1.end.y].sort((a, b) => a - b);
    const sortedY2 = [seg2.start.y, seg2.end.y].sort((a, b) => a - b);

    if (sortedY1[1] < sortedY2[0] || sortedY2[1] < sortedY1[0]) {
      return 0;
    }

    const overlapY = Math.min(sortedY1[1], sortedY2[1]) - Math.max(sortedY1[0], sortedY2[0]);
    return overlapY;
  }

  if (seg1.start.y === seg1.end.y && seg1.start.y === seg2.start.y) {
    const sortedX1 = [seg1.start.x, seg1.end.x].sort((a, b) => a - b);
    const sortedX2 = [seg2.start.x, seg2.end.x].sort((a, b) => a - b);

    if (sortedX1[1] < sortedX2[0] || sortedX2[1] < sortedX1[0]) {
      return 0;
    }

    const overlapX = Math.min(sortedX1[1], sortedX2[1]) - Math.max(sortedX1[0], sortedX2[0]);
    return overlapX;
  }

  const intersection = getSegmentIntersectionPoint(seg1, seg2);
  if (intersection && isPointOnSegment(intersection, seg1) && isPointOnSegment(intersection, seg2)) {
    return Math.sqrt(Math.pow(intersection.x - seg1.start.x, 2) + Math.pow(intersection.y - seg1.start.y, 2));
  }

  return 0;
}

export function areCollinearAndOverlap(p1, p2, p3, p4, minOverlap) {
  // Check the collinearity of the segments
  if (areCollinear(p1, p2, p3, p4)) {
    // If they are collinear, check for minimum overlap
    const seg1 = { start: p1, end: p2 };
    const seg2 = { start: p3, end: p4 };
    const overlapLength = getOverlapLength(seg1, seg2);

    return overlapLength >= minOverlap;
  }
  return false;
}

/**
 * Function for finding a point that is below and to the right of all other points
 * @param {Array} points {x, y}
 * @returns {Object} Point that is below and to the right of all others
 */
export function findLowestRightmostPoint(points) {
  if (!points.length) {
    return null;
  }

  let resultPoint = points[0];

  for (let i = 1; i < points.length; i++) {
    if (points[i].x > resultPoint.x) {
      resultPoint = points[i]; // Finding a new point to the right
    } else if (points[i].x === resultPoint.x && points[i].y < resultPoint.y) {
      resultPoint = points[i]; // If x are equal, choose a lower point
    }
  }

  return resultPoint;
}

/**
 * Calculate the length of the legs in an isosceles right triangle given the hypotenuse.
 * @param {number} hypotenuse - The length of the hypotenuse.
 * @returns {number} The length of each leg.
 */
export function calculateLeg(hypotenuse) {
  // Calculate the leg length using the relationship derived from Pythagoras' theorem
  return hypotenuse / Math.sqrt(2);
}


/**
 * Get closest point on the line
 * @returns {number} The length of each leg.
 * @param lineStart
 * @param lineEnd
 * @param mousePos
 */
export function getClosestPointOnLine(lineStart, lineEnd, mousePos) {
  const lineVec = {
    x: lineEnd.x - lineStart.x,
    y: lineEnd.y - lineStart.y
  };

  const lineLen = getPointDistance(lineStart, lineEnd);
  const lineUnitVec = {
    x: lineVec.x / lineLen,
    y: lineVec.y / lineLen
  };

  const mouseVec = {
    x: mousePos.x - lineStart.x,
    y: mousePos.y - lineStart.y
  };

  const dotProduct = mouseVec.x * lineUnitVec.x + mouseVec.y * lineUnitVec.y;
  const clampedDotProduct = Math.max(0, Math.min(dotProduct, lineLen));
  const x = lineStart.x + lineUnitVec.x * clampedDotProduct;
  const y = lineStart.y + lineUnitVec.y * clampedDotProduct;

  return {
    x: x,
    y: y,
  };
}

export const findTopLeftPoint = (points) => {
  return points.reduce((topLeft, point) => {
    if (point.y < topLeft.y || (point.y === topLeft.y && point.x < topLeft.x)) {
      return point;
    }
    return topLeft;
  }, points[0]);
};

export const adjustPointsRelativeToTopLeft = (points, topLeft) => {
  return points.map(point => ({
    x: point.x - topLeft.x,
    y: point.y - topLeft.y
  }));
};

export const sortPointsClockwise = (points) => {
  let topLeft, topRight, bottomLeft, bottomRight;

  // Функция для округления координат
  const roundCoordinate = (value, precision = 10) => {
    const factor = Math.pow(10, precision); // precision — количество знаков после запятой
    return Math.round(value * factor) / factor;
  };

  // Округляем координаты точек
  const roundedPoints = points.map(point => ({
    x: roundCoordinate(point.x),
    y: roundCoordinate(point.y)
  }));

  // Находим крайние значения для округленных координат
  const minX = Math.min(...roundedPoints.map(p => p.x));
  const maxX = Math.max(...roundedPoints.map(p => p.x));
  const minY = Math.min(...roundedPoints.map(p => p.y));
  const maxY = Math.max(...roundedPoints.map(p => p.y));

  // Разделяем точки по их координатам
  roundedPoints.forEach(point => {
    if (point.x === minX && point.y === minY) {
      topLeft = point;
    } else if (point.x === maxX && point.y === minY) {
      topRight = point;
    } else if (point.x === minX && point.y === maxY) {
      bottomLeft = point;
    } else if (point.x === maxX && point.y === maxY) {
      bottomRight = point;
    }
  });

  // Проверяем, что все точки определены, чтобы не было undefined
  if (!topLeft || !topRight || !bottomLeft || !bottomRight) {
    console.error("Points", points);
    console.error("One or more points are undefined", {topLeft, topRight, bottomLeft, bottomRight});
    return null;
  }

  // Возвращаем точки в порядке: верхняя левая, верхняя правая, нижняя правая, нижняя левая
  return [topLeft, topRight, bottomRight, bottomLeft];
};

export function calculatePolygonClippingPolygonArea(polygon) {
  if (!polygon || polygon.length === 0) return 0;

  let area = 0;
  const coordinates = polygon[0];

  for (let i = 0; i < coordinates.length - 1; i++) {
    const [x1, y1] = coordinates[i];
    const [x2, y2] = coordinates[i + 1];
    area += x1 * y2 - x2 * y1;
  }

  return Math.abs(area / 2);
}

export function findNearestPoint (targetPoint, points) {
  let nearestPoint = null;
  let minDistance = Infinity;

  for (const point of points) {
    const distance = Math.sqrt(
      Math.pow(point.x - targetPoint.x, 2) + Math.pow(point.y - targetPoint.y, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = point;
    }
  }

  return nearestPoint;
}

/**
 * Compares two arrays of points and returns true if at least one coordinate differs.
 * @param {Array} arr1 - First array of points.
 * @param {Array} arr2 - Second array of points.
 * @returns {boolean} - True if at least one coordinate is different, otherwise false.
 */
export function arePointsDifferent(arr1, arr2) {
  if (arr1.length !== arr2.length) return true;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i].x !== arr2[i].x || arr1[i].y !== arr2[i].y) {
      return true;
    }
  }

  return false;
}

export function getPointsCoords (shapeLines) {
  return shapeLines.map(line => { return {
    x: Math.round10(line.from.x, -4),
    y: Math.round10(line.from.y, -4),
    id: line.from.id
  }});
}

export function getRotatedPointV1 (point, aroundPoint, angle) {
  let ret = {x: 0, y: 0};
  let tr = {x: point.x - aroundPoint.x, y: point.y - aroundPoint.y};

  ret.x = tr.x * Math.cos(angle) - tr.y * Math.sin(angle);
  ret.y = tr.x * Math.sin(angle) + tr.y * Math.cos(angle);

  const xPos = Math.round10(Math.round10(ret.x, -5) + Math.round10(aroundPoint.x, -5), -5);
  const yPos = Math.round10(Math.round10(ret.y, -5) + Math.round10(aroundPoint.y, -5), -5);

  return {...point, x: xPos, y: yPos}
}

export function getPerpendicularPoint(basePoint, linePoint, distance, isLeft) {
  const dx = linePoint.x - basePoint.x;
  const dy = linePoint.y - basePoint.y;

  const length = Math.sqrt(dx * dx + dy * dy);
  const unitDx = dx / length;
  const unitDy = dy / length;

  const perpDx = -unitDy;
  const perpDy = unitDx;

  return {
    x: basePoint.x + (isLeft ? -perpDx : perpDx) * distance,
    y: basePoint.y + (isLeft ? -perpDy : perpDy) * distance
  };
}

// Returns a point offset at 'offsetDistance' perpendicular
// to the segment (base -> line). direction=+1 => left, -1 => right
export function getPerpOffset(basePoint, linePoint, offsetDist, dir) {
  const dx = linePoint.x - basePoint.x;
  const dy = linePoint.y - basePoint.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-12) {
    // Degenerate segment
    return { ...basePoint };
  }
  // Unit direction
  const ux = dx / len;
  const uy = dy / len;

  // direction=+1 => offset to left => (-uy, ux)
  // direction=-1 => offset to right => (uy, -ux)
  const perpX = -uy * dir;
  const perpY =  ux * dir;

  return {
    x: basePoint.x + perpX * offsetDist,
    y: basePoint.y + perpY * offsetDist
  };
}


// Returns line equation A x + B y = C
export function getLineEquation(p1, p2) {
  const A = p2.y - p1.y;
  const B = p1.x - p2.x;
  const C = A * p1.x + B * p1.y;
  return { A, B, C };
}

// Intersection of two lines
export function calculateIntersection(line1, line2) {
  const { A: A1, B: B1, C: C1 } = line1;
  const { A: A2, B: B2, C: C2 } = line2;
  const det = A1 * B2 - A2 * B1;
  if (Math.abs(det) < 1e-12) {
    // Parallel or coincident
    return null;
  }
  const x = (B2 * C1 - B1 * C2) / det;
  const y = (A1 * C2 - A2 * C1) / det;
  return { x, y };
}

// Returns angle in degrees at "curr" formed by (prev->curr) and (curr->next).
// Range: [0..360)
export function getAngleBetweenSegments(prev, curr, next) {
  const v1x = prev.x - curr.x;
  const v1y = prev.y - curr.y;
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;

  const dot   = v1x * v2x + v1y * v2y;
  const cross = v1x * v2y - v1y * v2x;
  const len1  = Math.sqrt(v1x * v1x + v1y * v1y);
  const len2  = Math.sqrt(v2x * v2x + v2y * v2y);

  if (len1 < 1e-12 || len2 < 1e-12) {
    return 0;
  }

  let cosVal = dot / (len1 * len2);
  if (cosVal > 1) cosVal = 1;
  if (cosVal < -1) cosVal = -1;

  let angleRad = Math.acos(cosVal);
  let angleDeg = (angleRad * 180) / Math.PI;

  // If cross < 0 => reflex angle
  if (cross < 0) {
    angleDeg = 360 - angleDeg;
  }
  return angleDeg;
}
